"use client";

/**
 * useAnalyseJobs — loads and manages the user's analysis job history.
 *
 * - Loads on mount via auth state listener (same pattern as React app)
 * - Polls every 5 seconds while any job is processing
 * - Exposes delete and rename operations with optimistic local updates
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { getFirebaseAuth } from "@/platform/firebase/auth";
import { listJobs, deleteJob as apiDeleteJob, renameJob as apiRenameJob } from "../api";
import type { AnalysisJob } from "../types";

interface UseAnalyseJobsReturn {
  jobs: AnalysisJob[];
  isLoading: boolean;
  reload: () => Promise<void>;
  deleteJob: (jobId: string) => Promise<void>;
  renameJob: (jobId: string, title: string) => Promise<void>;
}

export function useAnalyseJobs(): UseAnalyseJobsReturn {
  const [jobs, setJobs] = useState<AnalysisJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isAuthenticatedRef = useRef(false);

  const loadJobs = useCallback(async () => {
    try {
      const loaded = await listJobs(20);
      setJobs(loaded);
    } catch (err) {
      console.error("Error loading analyse jobs:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Auth listener → load on sign in, clear on sign out
  useEffect(() => {
    const auth = getFirebaseAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        isAuthenticatedRef.current = true;
        setIsLoading(true);
        loadJobs();
      } else {
        isAuthenticatedRef.current = false;
        setJobs([]);
        setIsLoading(false);
      }
    });
    return () => unsubscribe();
  }, [loadJobs]);

  // Poll every 5s while any job is processing
  useEffect(() => {
    const hasProcessing = jobs.some((j) => j.status === "processing");

    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (!hasProcessing) return;

    pollIntervalRef.current = setInterval(async () => {
      if (!isAuthenticatedRef.current) return;
      try {
        const loaded = await listJobs(20);
        setJobs(loaded);
      } catch {
        // silently ignore poll errors
      }
    }, 5000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [jobs.filter((j) => j.status === "processing").length]);

  const reload = useCallback(async () => {
    await loadJobs();
  }, [loadJobs]);

  const deleteJob = useCallback(async (jobId: string) => {
    await apiDeleteJob(jobId);
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  const renameJob = useCallback(async (jobId: string, title: string) => {
    await apiRenameJob(jobId, title);
    setJobs((prev) =>
      prev.map((j) => (j.id === jobId ? { ...j, title } : j))
    );
  }, []);

  return { jobs, isLoading, reload, deleteJob, renameJob };
}
